import { CirclePlus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useDebouncedValue } from '@/shared/lib/use-debounced-value';
import { MultiSelect } from '@/shared/ui';
import { useCompanyLabels } from '../api/use-company-labels';

type LabelsFilterProps = {
  values: string[];
  organizationIds?: string[];
  onChange: (labels: string[]) => void;
};

// Searchable multi-select over every account label (XT-892). There are
// thousands, so typing searches the server; chosen labels stay listed even
// when the current search no longer returns them.
export const LabelsFilter = ({
  values,
  organizationIds,
  onChange,
}: LabelsFilterProps) => {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 250);
  const { data, isFetching } = useCompanyLabels({
    search: debouncedSearch.trim(),
    organizationIds,
  });

  const options = useMemo(() => {
    const found = (data ?? []).map((row) => ({
      label: row.label,
      value: row.label,
      count: row.count,
    }));
    const listed = new Set(found.map((option) => option.value));
    const selected = values
      .filter((value) => !listed.has(value))
      .map((value) => ({ label: value, value }));
    return [...selected, ...found];
  }, [data, values]);

  return (
    <MultiSelect
      disableLocalFilter
      emptyMessage="No labels found"
      leadingIcon={<CirclePlus style={{ height: 16, width: 16 }} />}
      loading={isFetching}
      onChange={onChange}
      onSearchChange={setSearch}
      options={options}
      placeholder="Labels"
      searchPlaceholder="Search labels"
      showSelectAll={false}
      values={values}
    />
  );
};
